import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, apiErrorMessage } from "../../../lib/api";
import { AREA, AREA_CHOICES, IMPACT, IMPACT_ORDER, KIND, KIND_ORDER, pageContext } from "../../../lib/tickets";
import ScreenshotPicker, { ScreenshotWarning, uploadScreenshots } from "./ScreenshotPicker";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../ui/dialog";

const EMPTY = { kind: "", title: "", description: "", steps: "", impact: "", area: "" };

/**
 * The form a ticket is opened with (14.10.2026), from two places: the
 * "Raportează" button on top of every admin page, which knows the page it was
 * pressed on, and "Tichet nou" in the Tickets tab, where the area is picked
 * from a list. `via` tells the server which. `prefill` starts the form
 * with some fields written — "Lipsește ceva sau e greșit?" in the guide.
 */
export default function ReportDialog({ open, onClose, area = "", via = "tab", onCreated, prefill = null }) {
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const content = useRef(null);
  const fromPage = via === "report_button";

  useEffect(() => {
    if (open) { setForm({ ...EMPTY, area: fromPage ? area : "", ...(prefill || {}) }); setFiles([]); }
  }, [open, area, fromPage, prefill]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.kind) { toast.error("Alege ce fel de tichet e."); return; }
    if (!form.impact) { toast.error("Alege cât de tare te încurcă."); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/tickets", { ...form, area: form.area || "other", via, context: pageContext() });
      const failed = files.length ? await uploadScreenshots(r.data.id, files) : 0;
      toast.success(failed
        ? `Am primit tichetul ${r.data.label}, fără ${failed === 1 ? "o captură" : `${failed} capturi`}. Le poți adăuga din tichet.`
        : `Am primit tichetul ${r.data.label}. Îl găsești în tabul Tichete.`);
      onCreated?.(r.data);
      onClose();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Tichetul nu a putut fi trimis."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent ref={content} className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="ticket-report-dialog">
        <DialogHeader>
          <DialogTitle>{fromPage ? "Raportează" : "Tichet nou"}</DialogTitle>
          <DialogDescription>
            Ceva nu merge, ai o idee sau ceva ar putea merge mai bine? Scrie aici, ca să nu se piardă.
            {fromPage && area && <> Pagina: <strong>{AREA[area] || AREA.other}</strong>.</>}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <fieldset>
            <legend className="text-xs font-semibold text-[#373A45] mb-2">Ce fel de tichet e</legend>
            <div className="flex flex-wrap gap-2">
              {KIND_ORDER.map((key) => (
                <Choice key={key} name="ticket-kind" value={key} checked={form.kind === key}
                  onChange={set("kind")} label={KIND[key].label} testid={`ticket-kind-${key}`} />
              ))}
            </div>
          </fieldset>

          <Field label="Titlu" hint="O frază: „Exportul de înscrieri nu include grupa”">
            <input value={form.title} onChange={set("title")} maxLength={160} required
              className={INPUT} data-testid="ticket-title" />
          </Field>

          <Field label={form.kind === "bug" ? "Ce s-a întâmplat" : "Ce ai vrea"}>
            <textarea value={form.description} onChange={set("description")} rows={4} maxLength={5000} required
              className={INPUT} data-testid="ticket-description" />
          </Field>

          {form.kind === "bug" && (
            <Field label="Pașii până la problemă" hint="Opțional. De exemplu: Înscrieri → filtru Grupa Mai → Export">
              <textarea value={form.steps} onChange={set("steps")} rows={3} maxLength={3000}
                className={INPUT} data-testid="ticket-steps" />
            </Field>
          )}

          {!fromPage && (
            <Field label="Unde în platformă">
              <select value={form.area} onChange={set("area")} className={INPUT} data-testid="ticket-area">
                <option value="">Alege…</option>
                {AREA_CHOICES.map((key) => <option key={key} value={key}>{AREA[key]}</option>)}
              </select>
            </Field>
          )}

          <div>
            <span className="block text-xs font-semibold text-[#373A45] mb-1">Capturi de ecran (opțional)</span>
            <ScreenshotWarning />
            <ScreenshotPicker files={files} onChange={setFiles} pasteTarget={content} />
          </div>

          <fieldset>
            <legend className="text-xs font-semibold text-[#373A45] mb-2">Cât de tare te încurcă</legend>
            <div className="flex flex-wrap gap-2">
              {IMPACT_ORDER.map((key) => (
                <Choice key={key} name="ticket-impact" value={key} checked={form.impact === key}
                  onChange={set("impact")} label={IMPACT[key].label} testid={`ticket-impact-${key}`} />
              ))}
            </div>
          </fieldset>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} disabled={busy}
              className="rounded-full px-4 py-2 text-sm border border-[#E5E7EB] text-[#373A45] hover:bg-[#F5F6F6]">
              Renunță
            </button>
            <button type="submit" disabled={busy}
              className="rounded-full px-4 py-2 text-sm font-semibold bg-[#965EA7] text-white hover:bg-[#7A4986] disabled:opacity-60 inline-flex items-center gap-1.5"
              data-testid="ticket-submit">
              {busy && <Loader2 size={14} className="animate-spin" />} {busy && files.length ? "Se trimit capturile…" : "Trimite tichetul"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const INPUT = "w-full px-3 py-2 text-sm bg-white border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#965EA7] outline-none";

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-[#373A45] mb-1">{label}</span>
      {children}
      {hint && <span className="block mt-1 text-[11px] text-[#6B7280]">{hint}</span>}
    </label>
  );
}

function Choice({ name, value, checked, onChange, label, testid }) {
  return (
    <label className={`cursor-pointer rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors
      focus-within:ring-2 focus-within:ring-[#965EA7]
      ${checked ? "bg-[#373A45] text-white border-[#373A45]" : "border-[#E5E7EB] text-[#373A45] hover:bg-[#F5F6F6]"}`}>
      <input type="radio" name={name} value={value} checked={checked} onChange={onChange} className="sr-only" data-testid={testid} />
      {label}
    </label>
  );
}
